import {
  Button,
  Flex,
  Input,
  Modal,
  ScrollArea,
  Select,
  Text,
} from "@mantine/core";
import { FileWithPath, MIME_TYPES, PDF_MIME_TYPE } from "@mantine/dropzone";
import { isNotEmpty, useForm } from "@mantine/form";
import get from "lodash/get";
import noop from "lodash/noop";
import { useCallback } from "react";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import Dropzone from "../../components/Dropzone/Dropzone.tsx";
import { Loader } from "../../components/Loader.tsx";
import { getCompanyOptions } from "../../utils/company.ts";
import { DocumentType } from "./types.ts";

const DOCUMENT_FILE_TYPES = {
  [DocumentType.KTP]: {
    accept: [MIME_TYPES.jpeg, "image/jpg", MIME_TYPES.png],
    description: "Only .jpeg, .jpg, .png files up to 5MB are allowed",
    reason: "KTP documents should be uploaded as clear images",
  },
  [DocumentType.NPWP_COMPANY]: {
    accept: PDF_MIME_TYPE,
    reason: "NPWP Company documents should be uploaded as PDF files",
  },
  [DocumentType.NPWP_INDIVIDU]: {
    accept: PDF_MIME_TYPE,
    reason: "NPWP Individu documents should be uploaded as PDF files",
  },
  [DocumentType.REKENING_KORAN]: {
    accept: PDF_MIME_TYPE,
    reason: "Rekening Koran documents should be uploaded as PDF files",
  },
  [DocumentType.FAKTUR_PAJAK]: {
    accept: PDF_MIME_TYPE,
    reason: "Faktur Pajak documents should be uploaded as PDF files",
  },
  [DocumentType.INVOICE]: {
    accept: PDF_MIME_TYPE,
    reason: "Invoice documents should be uploaded as PDF files",
  },
  [DocumentType.DOKUMEN_PO]: {
    accept: PDF_MIME_TYPE,
    reason: "Purchase Order documents should be uploaded as PDF files",
  },
};

interface DocumentUploadModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (
    file: File,
    documentType: DocumentType,
    customerId: number
  ) => void;
}

const DocumentUploadModal = ({
  opened,
  onClose,
  onSubmit,
}: DocumentUploadModalProps) => {
  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data;

  const form = useForm<{
    documentType: DocumentType | null;
    customerId: string | null;
    file: File | null;
  }>({
    initialValues: {
      documentType: null,
      customerId: null,
      file: null,
    },
    validate: {
      documentType: isNotEmpty("Field is required"),
      customerId: isNotEmpty("Field is required"),
      file: isNotEmpty("Field is required"),
    },
    validateInputOnBlur: true,
    clearInputErrorOnChange: true,
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleDropFile = useCallback(
    (files: FileWithPath[]) => {
      form.setValues({ file: files[0] });
    },
    [form]
  );

  const handleRemoveFile = useCallback(() => {
    form.setValues({ file: null });
  }, [form]);

  const handleDocumentTypeChange = (value: string | null) => {
    form.setValues({ documentType: value as DocumentType | null });
    const { file } = form.values;
    if (file && value) {
      const allowedTypes = DOCUMENT_FILE_TYPES[value as DocumentType].accept;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (!allowedTypes.includes(file.type)) {
        handleRemoveFile();
      }
    }
  };

  const handleSubmit = () => {
    if (
      !form.isValid() ||
      !form.values.file ||
      !form.values.documentType ||
      !form.values.customerId
    ) {
      return;
    }

    const { documentType, customerId, file } = form.values;
    onSubmit(file, documentType, Number(customerId));
    form.reset();
  };

  if (fetchingCustomers) {
    return (
      <Modal
        opened={opened}
        title="Upload Document"
        size="lg"
        closeOnClickOutside={false}
        onClose={handleClose}
      >
        <Flex justify="center" align="center" style={{ minHeight: 200 }}>
          <Loader />
        </Flex>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      title="Upload Document"
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}
      closeOnClickOutside={false}
      onClose={handleClose}
    >
      <Flex direction="column" gap={20}>
        <Flex direction="column">
          <Flex direction="column">
            <Select
              label="Document Type"
              placeholder="Choose Document Type"
              data={Object.values(DocumentType).map((type) => ({
                value: type,
                label: type,
              }))}
              required
              {...form.getInputProps("documentType")}
              onChange={handleDocumentTypeChange}
            />
            <Text size="xs" mt={4}>
              {form.values.documentType
                ? DOCUMENT_FILE_TYPES[form.values.documentType].reason
                : "Please select a document type first"}
            </Text>
          </Flex>

          <Flex direction="column" mt={16} mb={16}>
            <Input.Wrapper required label="Document File" size="m" w="100%">
              <Dropzone
                maxWidth="100%"
                subLabel={get(
                  DOCUMENT_FILE_TYPES,
                  `${form.values.documentType}.description`
                )}
                disabled={!form.values.documentType}
                maxFiles={1}
                accept={get(
                  DOCUMENT_FILE_TYPES,
                  `${form.values.documentType}.accept`
                )}
                value={form.values.file ? [form.values.file] : null}
                onDrop={handleDropFile}
                onUploadSuccess={noop}
                onRemove={handleRemoveFile}
              />
            </Input.Wrapper>
          </Flex>

          <Select
            required
            searchable
            clearable
            label="Customer Name"
            placeholder="Select Customer Name"
            data={getCompanyOptions(customers)}
            disabled={fetchingCustomers}
            {...form.getInputProps("customerId")}
          />
        </Flex>

        <Flex gap={20} justify="end">
          <Button
            variant="outlineBlue"
            uppercase
            type="reset"
            onClick={onClose}
          >
            cancel
          </Button>
          <Button
            variant="filledBlue"
            uppercase
            type="submit"
            disabled={!form.isValid()}
            onClick={handleSubmit}
          >
            submit
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default DocumentUploadModal;
